/********************************************************************************
 * Copyright (c) 2026 CrossBreeze, EclipseSource and others.
 *
 * This program and the accompanying materials are made available under the
 * terms of the MIT License which is available in the project root.
 *
 * SPDX-License-Identifier: MIT
 ********************************************************************************/

import type { MessageConnection } from 'vscode-jsonrpc';
import { type ResolvedMessage, defineMessage, describeError, resolve } from '../messages/primitives';
import { type RpcProxy, createRpcProxy } from '../rpc';
import type { DataPort } from './data-port';

/**
 * The transport never opened. A complete sentence rather than a fragment: a
 * fragment is nested inside a sentence the framework does not own, so no
 * translator controls the whole and the composition cannot be made to read
 * correctly in every language.
 */
export const DATA_SERVER_CONNECT_FAILED = defineMessage(
   'hydranium/protocol/data-server-connect-failed',
   'Could not connect to the data server: {detail}'
);

export const DATA_SERVER_NOT_READY = defineMessage(
   'hydranium/protocol/data-server-not-ready',
   'The data server did not become ready: {detail}'
);

/** The one method a connection needs of any server: its startup gate. */
export interface ReadyServer {
   waitForReady(): Promise<void>;
}

/**
 * Lifecycle reporting, for a host that raises warm-up UI around the two waits.
 *
 * Nothing fires until something asks for the connection — the generation is
 * built on the first {@link RpcConnection.connected}. A host that wants the
 * sequence at startup drives that call itself, or a workspace where nobody
 * opens a document reports neither the connect nor the readiness its UI waits
 * on.
 */
export interface RpcConnectionLifecycle {
   /** A generation is opening its transport, including on each reconnect. */
   readonly onConnecting?: () => void;
   /** The server's readiness gate has settled for a generation. */
   readonly onReady?: () => void;
   /**
    * A generation failed to connect or to become ready. The failure is still
    * reported through {@link DataPort.reportError} and still rejects the
    * awaiting caller; this is for a host that also drives its own UI.
    */
   readonly onFailed?: (error: unknown) => void;
}

/** Calls each distinct lifecycle's hooks in order; one passed twice is called once. */
function composeLifecycles(...lifecycles: (RpcConnectionLifecycle | undefined)[]): RpcConnectionLifecycle {
   const present = [...new Set(lifecycles)].filter((lifecycle): lifecycle is RpcConnectionLifecycle => lifecycle !== undefined);
   return {
      onConnecting: () => present.forEach(lifecycle => lifecycle.onConnecting?.()),
      onReady: () => present.forEach(lifecycle => lifecycle.onReady?.()),
      onFailed: error => present.forEach(lifecycle => lifecycle.onFailed?.(error))
   };
}

/** Everything {@link RpcConnection} needs once a subclass has resolved its defaults. */
export interface ResolvedRpcConnectionOptions<TClient extends object> {
   readonly methodNamespace: string;
   readonly clientMethods: readonly (keyof TClient & string)[];
   readonly lifecycle: RpcConnectionLifecycle;
}

/**
 * One connection generation: its connection, its proxy, and its readiness.
 *
 * Named by the `protected` reconnect seams {@link RpcConnection.currentGeneration}
 * and {@link RpcConnection.awaitReady}, so an override has to name it too.
 */
export interface RpcConnectionGeneration<TServer extends object> {
   /**
    * The port's connection. {@link RpcConnection.currentGeneration} reports a
    * rejection to the port. An opened one is disposed by
    * {@link RpcConnection.dropGeneration}, and stays with the port when the
    * generation fails its readiness check.
    */
   readonly connection: Promise<MessageConnection>;
   /**
    * The proxy over {@link connection}, whose calls queue until it opens. Dead
    * once {@link RpcConnection.dropGeneration} disposes {@link connection}.
    */
   readonly server: RpcProxy<TServer>;
   /** Set on first use; the shared readiness gate for this generation. */
   ready?: Promise<void>;
}

/**
 * One JSON-RPC connection to a head, with the three jobs every host adapter
 * would otherwise re-derive above {@link DataPort}:
 *
 * 1. **Build the typed proxy** over the port's connection, with the caller's
 *    wire prefix and client-method allowlist.
 * 2. **Own the readiness gate** — `waitForReady` once per connection, shared
 *    across concurrent callers. A client can connect before the workspace walk
 *    finishes, and an early request is then answered correctly from an empty
 *    registry, which reads as a broken project tier rather than as a race.
 * 3. **Own the reconnect policy**, by dropping its generation when the port
 *    disposes and building a fresh one on the next request.
 *
 * Bounded only by {@link ReadyServer}, so a head serving a slice of the data
 * protocol — diagnostics alone, or one with methods excluded — is still a
 * legal server here. `DataConnection` narrows the bound because its sessions
 * call the document methods; nothing at this layer does.
 */
export class RpcConnection<TServer extends ReadyServer, TClient extends object> {
   /** The wire prefix of every generation's proxy, fixed for the connection's life. */
   protected readonly methodNamespace: string;
   /** The only methods of {@link localTarget} bound for the server to call, on each new generation. */
   protected readonly clientMethods: readonly (keyof TClient & string)[];
   /** The caller's hooks composed with the port's, so both hear each transition. */
   protected readonly lifecycle: RpcConnectionLifecycle;
   /** The current generation, or `undefined` before the first request / after a teardown. */
   protected generation?: RpcConnectionGeneration<TServer>;
   /** Set by {@link dispose}, after which {@link assertLive} throws. */
   protected disposed = false;
   /** Drops the generation when the port disposes. Released by {@link dispose}. */
   protected readonly portDisposeListener: { dispose(): void };

   constructor(
      protected readonly port: DataPort,
      protected readonly client: TClient,
      options: ResolvedRpcConnectionOptions<TClient>
   ) {
      this.methodNamespace = options.methodNamespace;
      this.clientMethods = options.clientMethods;
      this.lifecycle = composeLifecycles(port.connectionLifecycle, options.lifecycle);
      this.portDisposeListener = this.port.onDispose(() => this.dropGeneration());
   }

   /**
    * The connected, READY server proxy.
    *
    * Returns the proxy rather than `void` on purpose. A reconnect replaces the
    * proxy, so a caller that cached one from an earlier call would go on
    * addressing a dead connection with no error — handing it back per call
    * makes the stale reference unrepresentable.
    *
    * Concurrent callers share one readiness promise, so `waitForReady` is
    * awaited once per generation and not once per caller.
    */
   async connected(): Promise<RpcProxy<TServer>> {
      this.assertLive();
      const generation = this.currentGeneration();
      if (!generation.ready) {
         generation.ready = this.awaitReady(generation);
      }
      await generation.ready;
      // Dropped while it opened: its transport is gone, so the call goes to the
      // next generation, or rejects once this connection is disposed.
      if (this.generation !== generation) {
         return this.connected();
      }
      return generation.server;
   }

   /**
    * The current generation's proxy WITHOUT awaiting readiness — calls queue
    * against the connection promise.
    *
    * **Protected, and that is the point.** An early request is answered
    * correctly from a registry the workspace walk has not filled yet, which
    * reads as a broken project tier rather than as a race — so every caller has
    * to interpose the gate, and every caller forgetting to is a silent bug.
    * {@link connected} is the public route and returns this same proxy once the
    * gate has settled, which leaves nothing to forget. The one caller that
    * cannot use it is {@link awaitReady}, whose whole job is running the gate.
    *
    * Read per access, never cached: a reconnect replaces the generation, and a
    * held reference would address the dead one.
    */
   protected get server(): RpcProxy<TServer> {
      this.assertLive();
      return this.currentGeneration().server;
   }

   /**
    * Surface a failure the way this host does, through the port's sink.
    *
    * Here rather than only on the port so every participant sharing the
    * connection reports through one route without being handed the transport.
    */
   reportError(error: unknown, reported: ResolvedMessage): void {
      this.port.reportError(error, reported);
   }

   /** Tear down the current connection and stop tracking the port. Idempotent. */
   dispose(): void {
      if (this.disposed) {
         return;
      }
      this.disposed = true;
      this.portDisposeListener.dispose();
      this.dropGeneration();
   }

   /** The live generation, building one if there is none. */
   protected currentGeneration(): RpcConnectionGeneration<TServer> {
      if (this.generation) {
         return this.generation;
      }
      this.lifecycle.onConnecting?.();
      const connection = this.port.connect();
      // Rejection is reported here rather than left to float: an unhandled
      // rejection on a connection promise is the failure mode that reads as
      // "the model is empty" instead of "the transport never opened".
      connection.catch((error: unknown) =>
         this.port.reportError(error, resolve(DATA_SERVER_CONNECT_FAILED, { detail: describeError(error) }))
      );
      const server = createRpcProxy<TServer, TClient>(connection, {
         methodNamespace: this.methodNamespace,
         localTarget: this.localTarget(),
         localMethods: this.clientMethods
      });
      this.generation = { connection, server };
      return this.generation;
   }

   /**
    * The object whose {@link clientMethods} answer the server on each new
    * generation: {@link client} itself. A subclass that must see a call before
    * the client does returns an object that forwards each of them to it.
    */
   protected localTarget(): TClient {
      return this.client;
   }

   /** Await the connection and the server's startup gate for one generation. */
   protected async awaitReady(generation: RpcConnectionGeneration<TServer>): Promise<void> {
      let opened = false;
      try {
         await generation.connection;
         opened = true;
         // Dropped meanwhile: writing the request would hit a closed transport.
         if (this.generation !== generation) {
            return;
         }
         await generation.server.waitForReady();
         // Before `onReady`, so a failing restore reports a failure alone.
         this.generationReady(generation);
         this.lifecycle.onReady?.();
      } catch (error: unknown) {
         // A dropped generation's request fails with its transport, which says
         // nothing about whether the server is ready.
         if (this.generation !== generation) {
            return;
         }
         this.lifecycle.onFailed?.(error);
         // Drop the generation so the next request retries rather than
         // re-awaiting a settled rejection forever.
         if (this.generation === generation) {
            this.generation = undefined;
         }
         // A connection that never opened is already reported as a failed connect.
         if (opened) {
            this.port.reportError(error, resolve(DATA_SERVER_NOT_READY, { detail: describeError(error) }));
         }
         throw error;
      }
   }

   /**
    * Called once `generation` has passed its readiness gate, whichever request
    * brought it up. A generation can replace one that failed at readiness
    * without {@link dropGeneration}, so this, not a drop, is where state kept
    * on the server is put back. It runs inside the readiness gate, so a throw
    * from an override fails the generation as a refused gate would.
    */
   protected generationReady(_generation: RpcConnectionGeneration<TServer>): void {
      // Nothing to put back at this layer.
   }

   /**
    * Discard the current generation, disposing its connection if it opened.
    * The next {@link connected} builds a fresh one.
    */
   protected dropGeneration(): void {
      const generation = this.generation;
      this.generation = undefined;
      if (!generation) {
         return;
      }
      generation.connection.then(connection => connection.dispose()).catch(() => undefined);
   }

   /** Throw once disposed, a plain `Error` naming the class rather than a protocol error. */
   protected assertLive(): void {
      if (this.disposed) {
         throw new Error(`${this.constructor.name} is disposed`);
      }
   }
}
